import { ReactNode } from 'react';
import cx from 'classnames';

export interface TabItem<Id extends string | number = string> {
  id: Id;
  label: ReactNode;
  disabled?: boolean;
  /** A short note shown above the tab on hover — «Раздел в разработке». */
  hint?: string;
  /** `folder` only: a free-standing action at the end of the row («Добавить»), framed on all sides. */
  detached?: boolean;
}

export interface TabListProps<Id extends string | number = string> {
  items: TabItem<Id>[];
  value: Id | undefined;
  onChange: (id: Id) => void;
  /** `underline` switches the sections of a page; `segmented` is a compact switch inside one;
   * `pills` sits in a white card and wraps onto a second row instead of scrolling; `folder` is a row of
   * framed tabs over a panel; `raised` is a white track on a phone with a red active tab. */
  variant?: 'underline' | 'segmented' | 'pills' | 'folder' | 'raised';
  /** `underline` only: `primary` keeps the idle tabs red too. */
  tone?: 'default' | 'primary';
  /** Tabs share the row equally and stack into a column below `sm` — for two or three long labels. */
  stretch?: boolean;
  className?: string;
}

const listClasses = {
  underline: 'flex min-w-0 gap-2 overflow-x-auto overscroll-x-contain border-b border-line',
  segmented: 'flex w-fit max-w-full gap-1 overflow-x-auto rounded-control bg-surface-muted p-1',
  pills: 'flex flex-wrap items-center gap-1 rounded-control border border-line bg-surface px-3 py-2',
  folder: 'flex flex-wrap gap-2 border-b border-line pb-3',
  raised: 'flex gap-2 overflow-x-auto rounded-card bg-surface p-1 shadow-track',
};

const tabClasses = {
  underline: {
    base: 'border-b-2 px-3 py-2 text-label lg:px-6',
    active: 'border-primary text-primary',
    idle: 'border-transparent text-ink-muted hover:border-accent',
  },
  segmented: {
    base: 'rounded-control px-5 py-2 text-ui-strong',
    active: 'bg-surface text-ink shadow-control',
    idle: 'text-ink-muted hover:text-ink',
  },
  pills: {
    base: 'inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-ui-strong',
    active: 'bg-accent-bright text-white',
    idle: 'text-ink-muted hover:bg-surface-muted hover:text-ink',
  },
  folder: {
    base: 'inline-flex items-center gap-1.5 rounded-t-control border px-3 py-2 text-ui-xs-strong',
    active: 'border-report-accent border-b-surface bg-surface text-ink',
    idle: 'border-line text-ink-muted hover:bg-surface-muted',
  },
  raised: {
    base: 'min-h-11 flex-none touch-manipulation rounded-control px-4 py-2 text-ui-strong',
    active: 'bg-primary text-white shadow-primary-glow',
    idle: 'text-ink-muted',
  },
};

const detachedClasses = {
  base: 'inline-flex items-center gap-1.5 rounded-control border px-3 py-2 text-ui-xs',
  active: 'border-report-accent bg-surface text-ink',
  idle: 'border-line text-ink-muted hover:bg-surface-muted',
};

/** The tab bar on its own: the caller owns the active id and renders the panel. */
export const TabList = <Id extends string | number = string>({
  items,
  value,
  onChange,
  variant = 'underline',
  tone = 'default',
  stretch,
  className,
}: TabListProps<Id>) => {
  const classes = tabClasses[variant];

  return (
    <div role="tablist" className={cx(listClasses[variant], stretch && 'w-full flex-col sm:flex-row', className)}>
      {items.map((item) => {
        const active = item.id === value;
        const look = variant === 'folder' && item.detached ? detachedClasses : classes;
        const idle =
          variant === 'underline' && tone === 'primary'
            ? 'border-transparent text-primary hover:border-accent'
            : look.idle;

        const tab = (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            className={cx(
              'cursor-pointer transition-colors',
              stretch
                ? 'inline-flex flex-1 items-center gap-2 text-left sm:justify-center sm:text-center'
                : 'shrink-0 whitespace-nowrap',
              'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary',
              'disabled:cursor-not-allowed disabled:text-ink-subtle disabled:hover:border-transparent',
              look.base,
              active ? look.active : idle,
            )}
          >
            {item.label}
          </button>
        );
        if (!item.hint) return tab;

        return (
          <div key={item.id} className="group relative shrink-0">
            {tab}
            <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 hidden -translate-x-1/2 group-hover:flex">
              <div className="whitespace-nowrap rounded-control bg-surface-inverse px-3 py-1.5 text-ui-xs text-white shadow-popup">
                {item.hint}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
